import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2, Paperclip, Plus, Upload, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import {
    getUploadUrl,
    uploadFile,
    deleteFile,
    getDepartmentFiles,
    getUserFiles,
} from '../../services/api/fileService';
import { getFileMimeType, useValidateFile } from '../../utils/files';
import { FileType } from './types';
import FileModal from '../video-overview/FileModal';

interface AttachmentUploadDialogProps {
    defaultFiles?: string[];
    onFilesSelected: (fileIds: string[]) => void;
    ownerType?: 'department' | 'user';
    allowMultiple?: boolean;
    allowedTypes?: {
        document?: boolean;
        image?: boolean;
        video?: boolean;
        other?: boolean;
    };
    disabled?: boolean;
}

export function AttachmentUploadDialog({
    defaultFiles = [],
    onFilesSelected,
    ownerType = 'department',
    allowMultiple = true,
    allowedTypes = { document: true, image: true, video: false },
    disabled = false,
}: AttachmentUploadDialogProps) {
    const { t } = useTranslation();
    const [allFiles, setAllFiles] = useState<FileType[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>(defaultFiles);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadNotice, setUploadNotice] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState<FileType | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropRef = useRef<HTMLDivElement>(null);

    const [_errors, _addError, _clearErrors, _removeError, validateFile] =
        useValidateFile(allowedTypes);

    const fetchFiles = useCallback(async () => {
        try {
            const files =
                ownerType === 'department'
                    ? await getDepartmentFiles()
                    : await getUserFiles();
            const filtered = (files || []).filter(
                (f: FileType) => allowedTypes[f.documentType as keyof typeof allowedTypes]
            );
            setAllFiles(filtered);
        } catch {
            setAllFiles([]);
        }
    }, [ownerType]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    useEffect(() => {
        setSelectedIds(defaultFiles);
    }, [defaultFiles]);

    const updateSelection = (ids: string[]) => {
        setSelectedIds(ids);
        onFilesSelected(ids);
    };

    const handleUpload = async (files: FileList) => {
        setError(null);
        setUploadNotice(null);
        const filesArray = Array.from(files);
        let uploadedCount = 0;

        for (const f of filesArray) {
            if (!validateFile(f)) {
                setError(
                    t('attachments.invalidFileType', { name: f.name })
                );
                continue;
            }

            setIsUploading(true);
            try {
                const uploadInfo = await getUploadUrl({
                    ownerType,
                    filename: f.name,
                    fileType: getFileMimeType(f),
                });

                if (!uploadInfo.url) {
                    setError(t('attachments.uploadUrlFailed'));
                    continue;
                }

                const response = await uploadFile(uploadInfo.url, f);
                if (response === 'OK') {
                    await fetchFiles();
                    if (uploadInfo.file?._id) {
                        const newIds = allowMultiple
                            ? [...selectedIds, uploadInfo.file._id]
                            : [uploadInfo.file._id];
                        updateSelection(newIds);
                        uploadedCount += 1;
                    }
                } else {
                    if (uploadInfo.file?._id) deleteFile(uploadInfo.file._id);
                    setError(
                        t('attachments.uploadFailed', { name: f.name })
                    );
                }
            } catch {
                setError(
                    t('attachments.uploadFailed', { name: f.name })
                );
            } finally {
                setIsUploading(false);
            }
        }

        if (uploadedCount > 0) {
            setUploadNotice(
                uploadedCount === 1
                    ? t('attachments.uploadSuccessSingle')
                    : t('attachments.uploadSuccessMany', {
                          count: uploadedCount,
                      })
            );
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) {
            handleUpload(e.dataTransfer.files);
        }
    };

    const handleRemove = (fileId: string) => {
        updateSelection(selectedIds.filter((id) => id !== fileId));
    };

    const handlePreviewFile = (file: FileType) => {
        setDialogOpen(false);
        setPreviewFile(file);
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
        setDialogOpen(true);
    };

    const selectedFiles = selectedIds
        .map((id) => allFiles.find((f) => f._id === id))
        .filter(Boolean) as FileType[];

    const unselectedFiles = allFiles.filter((f) => !selectedIds.includes(f._id));

    const truncateName = (name: string, max = 20) =>
        name.length > max ? name.slice(0, max - 1) + '\u2026' : name;

    const dialogManagerContent = (
        <div
            ref={dropRef}
            onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
            }}
            onDragLeave={(e) => {
                if (
                    dropRef.current &&
                    !dropRef.current.contains(e.relatedTarget as Node)
                ) {
                    setIsDragging(false);
                }
            }}
            onDrop={handleDrop}
            className={cn('space-y-4', disabled && 'opacity-50 pointer-events-none')}
        >
            <Button
                type="button"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                    'group flex h-auto w-full flex-col items-center justify-center rounded-xl border border-dashed px-6 py-10 text-center transition-colors duration-200 hover:bg-transparent',
                    isDragging
                        ? 'border-blue bg-blue/10'
                        : 'border-subtle/60 bg-muted/20 hover:border-blue/70 hover:bg-blue/5'
                )}
            >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue/10 text-blue transition-transform duration-200 group-hover:scale-105">
                    <Upload className="h-6 w-6" />
                </div>
                <div className="mt-4 text-base font-semibold text-foreground">
                    {isUploading
                        ? t('attachments.uploadingFiles')
                        : t('attachments.dropzoneCta')}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                    {t('attachments.uploadHint')}
                </div>
            </Button>

            {uploadNotice && !isUploading && (
                <div className="rounded-md border border-subtle/60 bg-blue/5 px-3 py-2 text-sm text-foreground">
                    {uploadNotice}
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-foreground">
                        {selectedFiles.length === 0
                            ? t('attachments.noneSelected')
                            : t('attachments.selectedCount', {
                                  count: selectedFiles.length,
                              })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {t('attachments.librarySubhint')}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {isUploading && (
                        <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            <span>
                                {t('attachments.uploadingInline')}
                            </span>
                        </div>
                    )}

                    {unselectedFiles.length > 0 && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    {t('attachments.chooseExisting')}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-1" align="end">
                                <div className="max-h-64 overflow-y-auto">
                                    {unselectedFiles.map((file) => (
                                        <button
                                            key={file._id}
                                            type="button"
                                            onClick={() =>
                                                updateSelection(
                                                    allowMultiple
                                                        ? [
                                                              ...selectedIds,
                                                              file._id,
                                                          ]
                                                        : [file._id]
                                                )
                                            }
                                            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
                                        >
                                            <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                            <span className="truncate">
                                                {file.title || file.filename}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            </div>

            {selectedFiles.length > 0 && (
                <div className="space-y-2">
                    {selectedFiles.map((file) => (
                        <div
                            key={file._id}
                            className="flex items-center gap-3 rounded-xl border border-subtle/60 bg-card/40 px-3 py-3"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue/10 text-blue">
                                <FileText className="h-4 w-4" />
                            </div>

                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => handlePreviewFile(file)}
                                className="min-w-0 flex-1 justify-start px-0 py-0 h-auto text-left hover:bg-transparent"
                            >
                                <div className="truncate text-sm font-medium text-foreground hover:underline">
                                    {file.title || file.filename}
                                </div>
                                {file.title &&
                                    file.title !== file.filename && (
                                        <div className="truncate text-xs text-muted-foreground">
                                            {truncateName(
                                                file.filename,
                                                64
                                            )}
                                        </div>
                                    )}
                            </Button>

                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove(file._id);
                                }}
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                aria-label={t('attachments.removeAria', {
                                    name: file.title || file.filename,
                                })}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {error && <p className="text-xs text-destructive">{error}</p>}

            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple={allowMultiple}
                onChange={(e) => {
                    if (e.target.files) handleUpload(e.target.files);
                    e.target.value = '';
                }}
            />
        </div>
    );

    const dialogSummary =
        selectedFiles.length === 0
            ? t('attachments.summaryEmpty')
            : t('attachments.summaryCount', {
                  count: selectedFiles.length,
              });

    return (
        <>
            {previewFile && (
                <FileModal
                    file={{
                        ...previewFile,
                        title: previewFile.title || previewFile.filename,
                    }}
                    close={handleClosePreview}
                />
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between text-left"
                    >
                        <span className="truncate text-foreground">
                            {dialogSummary}
                        </span>
                        <span className="text-xs font-medium text-muted-foreground">
                            {t('attachments.manage')}
                        </span>
                    </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {t('attachments.dialogTitle')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('attachments.dialogDescription')}
                        </DialogDescription>
                    </DialogHeader>
                    {dialogManagerContent}
                </DialogContent>
            </Dialog>
        </>
    );
}
